import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import Table from '@/shared/components/Table/Table'
import DatePicker, {
  DateRangeValue
} from '@/shared/components/DatePicker/DatePicker'
import Select from '@/shared/components/Select/Select'
import Button from '@/shared/components/Button/Button'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { TABLE_KEYS } from '@/shared/components/Table/models/Filter.model'
import {
  SalesReportsAPI,
  useIngredientsSalesTable,
  useSalesReportsColumnsConfig
} from '@/features/sales-reports'
import styles from '@/features/sales-reports/SalesReports.module.scss'

type TypeFilter = 'count' | 'weight' | 'volume' | null

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function InventorySalesTablePage() {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language || 'en'

  const [dateRange, setDateRange] = useState<DateRangeValue>(() => {
    const end = dayjs()
    const start = end.subtract(7, 'day')
    return [start, end]
  })

  const typeFilterOptions = useMemo<OptionType[]>(
    () => [
      { label: t('SALES_REPORTS.FILTER.MEASUREMENT.ALL'), value: '' },
      { label: t('SALES_REPORTS.FILTER.MEASUREMENT.COUNT'), value: 'count' },
      { label: t('SALES_REPORTS.FILTER.MEASUREMENT.WEIGHT'), value: 'weight' },
      { label: t('SALES_REPORTS.FILTER.MEASUREMENT.VOLUME'), value: 'volume' }
    ],
    [t]
  )

  const [typeFilter, setTypeFilter] = useState<OptionType>(typeFilterOptions[0])

  const [startDayjs, endDayjs] = dateRange
  const startDate = startDayjs?.format('YYYY-MM-DD') ?? ''
  const endDate = endDayjs?.format('YYYY-MM-DD') ?? ''

  const apiTypeFilter = (typeFilter.value as TypeFilter) || null

  const { data: ingredientsSalesTableByLanguage } = useIngredientsSalesTable({
    startDate,
    endDate,
    limit: 100,
    typeFilter: apiTypeFilter ?? undefined
  })

  const columns = useSalesReportsColumnsConfig()
  const columnsModel = ['number', 'name', 'price', 'quantity', 'percentage']

  const rows = useMemo(() => {
    if (!ingredientsSalesTableByLanguage?.items) return []
    return ingredientsSalesTableByLanguage.items.map((it, idx) => ({
      id: it.itemId || idx,
      number: idx + 1,
      name:
        (it.translations && it.translations[currentLanguage]) ||
        Object.values(it.translations ?? {})[0] ||
        '',
      price: it.price,
      quantity: it.quantity,
      percentage: it.percentage
    }))
  }, [ingredientsSalesTableByLanguage, currentLanguage])

  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)

  const canExport = Boolean(startDate && endDate)

  const handleExportPdf = async () => {
    if (!canExport) return
    setIsExportingPdf(true)
    try {
      const blob = await SalesReportsAPI.getInventoryPdf({
        language: currentLanguage,
        startDate,
        endDate,
        limit: 100,
        typeFilter: apiTypeFilter ?? undefined
      })
      downloadBlob(blob, `inventory-sales-${startDate}-${endDate}.pdf`)
    } finally {
      setIsExportingPdf(false)
    }
  }

  const handleExportExcel = async () => {
    if (!canExport) return
    setIsExportingExcel(true)
    try {
      const blob = await SalesReportsAPI.getIngredientsSalesExcel({
        language: currentLanguage,
        startDate,
        endDate,
        limit: 100,
        typeFilter: apiTypeFilter ?? undefined
      })
      downloadBlob(blob, `inventory-sales-${startDate}-${endDate}.xlsx`)
    } finally {
      setIsExportingExcel(false)
    }
  }

  return (
    <div className={styles.subpageWrapper}>
      <div className={styles.controlsRow}>
        <div className={styles.filtersGroup}>
          <DatePicker
            label={t('COMMON.DATE_RANGE')}
            value={dateRange}
            onChange={setDateRange}
          />
          <Select
            label={t('SALES_REPORTS.FILTER.MEASUREMENT_TYPE')}
            options={typeFilterOptions}
            value={typeFilter}
            onChange={value => {
              if (value && !Array.isArray(value)) {
                setTypeFilter(value as OptionType)
              } else if (!value) {
                setTypeFilter(typeFilterOptions[0])
              }
            }}
            hideClearIcon
          />
        </div>
        <div className={styles.exportGroup}>
          <Button
            label={t('SALES_REPORTS.EXPORT.PDF')}
            variant="text"
            color="primary"
            icon="las la-file-pdf"
            onClick={handleExportPdf}
            disabled={!canExport || isExportingPdf}
          />
          <Button
            label={t('SALES_REPORTS.EXPORT.EXCEL')}
            variant="text"
            color="primary"
            icon="las la-file-excel"
            onClick={handleExportExcel}
            disabled={!canExport || isExportingExcel}
          />
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <Table
          key={`inventory-${currentLanguage}`}
          initialRows={rows}
          columns={columns}
          columnsModel={columnsModel}
          tableKey={TABLE_KEYS.INGREDIENTS_SALES_REPORT}
          filterModel={{ items: [] }}
          hideToolbar
          hideCheckboxSelection
          isLoading={!ingredientsSalesTableByLanguage && canExport}
        />
      </div>
    </div>
  )
}

export default InventorySalesTablePage
